import React, { useEffect, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Typography,
} from '@mui/material';
import Grid2 from '@mui/material/Grid2';
import AddIcon from '@mui/icons-material/Add';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import RuleIcon from '@mui/icons-material/Rule';
import { useSelector } from 'react-redux';
import PageHeader from '../../components/PageHeader';
import { themeBackgrounds } from '../../theme/theme';
import CreateChatBot from './CreateChatBot';
import CreateAIChatBot from './CreateAIChatBot';
import useApplicationChatBots, { ChatBotItem } from '../../hooks/useApplicationChatBots';
import { aiChatBotKnowledgeService, KnowledgeBase } from '../../services/aiChatBotKnowledgeService';
import { voiceAssistantService } from '../../services/voiceAssistantService';

const primaryButtonSx = {
  background: themeBackgrounds.default,
  color: '#ffffff',
  '&:hover': { background: themeBackgrounds.default, opacity: 0.9 },
};

const AIChatBot: React.FC = () => {
  const organizationId = useSelector((state: any) => state.auth.userDetails?.organization_id) || '';
  const { bots, isLoadingBots, fetchChatBots, appId } = useApplicationChatBots();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [selectedBot, setSelectedBot] = useState<ChatBotItem | null>(null);
  const [aiConfigs, setAiConfigs] = useState<Record<string, KnowledgeBase>>({});
  const [assistantStatuses, setAssistantStatuses] = useState<Record<string, string>>({});
console.log('Application Id', appId);
  useEffect(() => {
    setShowCreateForm(false);
    setSelectedBot(null);
    setAiConfigs({});
    setAssistantStatuses({});
  }, [appId]);

  useEffect(() => {
    if (!showCreateForm && !selectedBot) {
      fetchChatBots();
    }
  }, [fetchChatBots, showCreateForm, selectedBot]);

  useEffect(() => {
    if (!organizationId || !appId || bots.length === 0) return;
    let cancelled = false;
    Promise.allSettled(
      bots.map((bot) => aiChatBotKnowledgeService.getKnowledgeBase({
        organization_id: organizationId,
        application_id: appId,
        bot_id: bot.bot_id,
      }))
    ).then((results) => {
      if (cancelled) return;
      const next: Record<string, KnowledgeBase> = {};
      results.forEach((result, index) => {
        if (result.status === 'fulfilled') {
          next[bots[index].bot_id] = result.value.data.knowledgeBase;
        }
      });
      setAiConfigs(next);
    });
    return () => {
      cancelled = true;
    };
  }, [bots, organizationId, appId]);

  useEffect(() => {
    if (!organizationId || !appId || bots.length === 0) return;
    let cancelled = false;
    Promise.allSettled(
      bots.map((bot) => voiceAssistantService.get({
        organization_id: organizationId,
        application_id: appId,
        bot_id: bot.bot_id,
      }))
    ).then((results) => {
      if (cancelled) return;
      const next: Record<string, string> = {};
      results.forEach((result, index) => {
        if (result.status === 'fulfilled' && result.value.exists && result.value.voice_assistant) {
          next[bots[index].bot_id] = result.value.voice_assistant.status;
        }
      });
      setAssistantStatuses(next);
    });
    return () => {
      cancelled = true;
    };
  }, [bots, organizationId, appId]);

  if (showCreateForm) {
    return (
      <CreateChatBot
        onCancel={() => setShowCreateForm(false)}
        onCreated={() => setShowCreateForm(false)}
      />
    );
  }

  if (selectedBot) {
    return (
      <CreateAIChatBot
        bot={selectedBot}
        onBack={() => setSelectedBot(null)}
      />
    );
  }

  return (
    <Box sx={{ width: '100%', mx: 'auto' }}>
      <PageHeader
        heading="AI Chat Bot"
        subheading="Activate AI on your chat bots. Give each bot instructions, a knowledge base, and an AI assistant so it can answer user questions on its own."
        variant="gradient"
      />

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          AI Chat Bot
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setShowCreateForm(true)}
          sx={primaryButtonSx}
        >
          Create
        </Button>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3, width: '100%', textAlign: 'left' }}>
        Select a chat bot to activate the AI bot feature, then add instructions, a knowledge base, or an AI assistant.
      </Typography>

      {isLoadingBots ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>Loading chat bots...</Typography>
        </Box>
      ) : bots.length === 0 ? (
        <Card
          sx={{
            borderRadius: 3,
            border: '1px dashed #c5d8f7',
            backgroundColor: '#f8fbff',
            py: 6,
          }}
        >
          <CardContent sx={{ textAlign: 'center' }}>
            <Avatar
              sx={{
                width: 64,
                height: 64,
                mx: 'auto',
                mb: 2,
                background: themeBackgrounds.default,
              }}
            >
              <AutoAwesomeIcon sx={{ fontSize: 32 }} />
            </Avatar>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
              No chat bots yet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Create a chat bot first, then activate the AI bot feature on it.
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setShowCreateForm(true)}
              sx={primaryButtonSx}
            >
              Create
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Grid2 container spacing={3}>
          {bots.map((bot) => {
            const config = aiConfigs[bot.bot_id];
            const isAiActive = config?.status === 'Active';
            const assistantStatus = assistantStatuses[bot.bot_id];
            const savedPreview = config?.instructions.mode === 'points'
              ? (config.instructions.points || []).join(' • ')
              : config?.instructions.mode === 'both'
                ? [config.instructions.paragraph, (config.instructions.points || []).join(' • ')].filter(Boolean).join(' • ')
                : config?.instructions.paragraph;
            const instructionPreview = savedPreview
              || 'Click to add instructions and a knowledge base to activate AI for this bot.';
            return (
              <Grid2 key={bot.id} size={{ xs: 12, sm: 6, md: 4 }}>
                <Card
                  onClick={() => setSelectedBot(bot)}
                  sx={{
                    height: '100%',
                    borderRadius: 3,
                    position: 'relative',
                    overflow: 'hidden',
                    cursor: 'pointer',
                    border: '1px solid rgba(0, 122, 255, 0.12)',
                    boxShadow: '0 8px 24px rgba(0, 122, 255, 0.08)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-6px)',
                      boxShadow: '0 16px 32px rgba(0, 122, 255, 0.16)',
                    },
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: '4px',
                      background: themeBackgrounds.default,
                    },
                  }}
                >
                  <CardContent sx={{ pt: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2, gap: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                        <Avatar sx={{ bgcolor: bot.avatarColor, color: '#1976d2', fontWeight: 700 }}>
                          {bot.bot_name.charAt(0).toUpperCase()}
                        </Avatar>
                        <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.3 }} noWrap>
                          {bot.bot_name}
                        </Typography>
                      </Box>
                      <Chip
                        size="small"
                        label={bot.status || 'Active'}
                        sx={{
                          background: themeBackgrounds.default,
                          color: '#ffffff',
                          fontWeight: 600,
                        }}
                      />
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 2 }}>
                      <RuleIcon sx={{ fontSize: 18, color: '#007aff', mt: 0.3 }} />
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}
                      >
                        {instructionPreview}
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      <Chip
                        size="small"
                        variant="outlined"
                        icon={<AutoAwesomeIcon sx={{ fontSize: 16 }} />}
                        label={isAiActive ? 'AI Active' : 'AI Inactive'}
                        sx={isAiActive
                          ? { borderColor: '#2e7d32', color: '#2e7d32', '& .MuiChip-icon': { color: '#2e7d32' } }
                          : { borderColor: '#9e9e9e', color: 'text.secondary' }}
                      />
                      <Chip
                        size="small"
                        variant="outlined"
                        label={`Files ${config?.counts.files || 0}`}
                        sx={{ borderColor: '#007aff', color: '#007aff' }}
                      />
                      <Chip
                        size="small"
                        variant="outlined"
                        label={`Text ${config?.counts.texts || 0}`}
                        sx={{ borderColor: '#5fb0ff', color: '#007aff' }}
                      />
                      <Chip
                        size="small"
                        variant="outlined"
                        label={`Links ${config?.counts.links || 0}`}
                        sx={{ borderColor: '#667eea', color: '#667eea' }}
                      />
                      {assistantStatus && (
                        <Chip
                          size="small"
                          variant="outlined"
                          icon={<RecordVoiceOverIcon sx={{ fontSize: 16 }} />}
                          label={assistantStatus === 'Active' ? 'Assistant Active' : 'Assistant Inactive'}
                          sx={assistantStatus === 'Active'
                            ? { borderColor: '#7c3aed', color: '#7c3aed', '& .MuiChip-icon': { color: '#7c3aed' } }
                            : { borderColor: '#9e9e9e', color: 'text.secondary' }}
                        />
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid2>
            );
          })}
        </Grid2>
      )}
    </Box>
  );
};

export default AIChatBot;
